import React from "react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Tag, Plus } from "lucide-react";
import TagInputDialog from "../tagInput";
import { useItemDetailsContext } from "./ItemDetailsContext";

const ItemDetailsTagsSection = () => {
    const {
        tags,
        openTag,
        recentTags,
        setRecentTags,
        itemData,
        handleTagDialogClose,
        handleTagSave,
        handleTagDialogOpen,
    } = useItemDetailsContext();

    // Generate consistent colors for tags based on their name
    const getTagColor = (tagName) => {
        const colors = [
            "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/25 hover:bg-blue-500/25",
            "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25 hover:bg-emerald-500/25",
            "bg-violet-500/15 text-violet-700 dark:text-violet-400 border-violet-500/25 hover:bg-violet-500/25",
            "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/25 hover:bg-amber-500/25",
            "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/25 hover:bg-rose-500/25",
            "bg-cyan-500/15 text-cyan-700 dark:text-cyan-400 border-cyan-500/25 hover:bg-cyan-500/25",
            "bg-pink-500/15 text-pink-700 dark:text-pink-400 border-pink-500/25 hover:bg-pink-500/25",
            "bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/25 hover:bg-orange-500/25",
        ];
        const hash = tagName.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
        return colors[hash % colors.length];
    };

    return (
        <>
            {/* Tags section with header */}
            <div className="space-y-3">
                <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium text-muted-foreground">Tags</span>
                </div>
                
                <div className="flex flex-wrap items-center gap-2">
                    {tags?.length > 0 ? (
                        <>
                            {tags.map((tag, index) => (
                                <Badge
                                    key={index}
                                    variant="outline"
                                    className={cn(
                                        "text-xs font-medium px-2.5 py-0.5 transition-colors cursor-default",
                                        getTagColor(tag.name)
                                    )}
                                >
                                    {tag.name}
                                </Badge>
                            ))}
                        </>
                    ) : (
                        <span className="text-sm text-muted-foreground">No tags</span>
                    )}
                    
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs gap-1 border-dashed"
                                onClick={handleTagDialogOpen}
                            >
                                <Plus className="h-3 w-3" />
                                Add Tag
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Add or manage tags</TooltipContent>
                    </Tooltip>
                </div>
            </div>

            {/* Tag input dialog */}
            <TagInputDialog
                open={openTag}
                onClose={handleTagDialogClose}
                onSave={handleTagSave}
                existingTags={tags}
                recentTags={recentTags}
                setRecentTags={setRecentTags}
                id={itemData.id}
                type={"add_tag_to_item"}
            />
        </>
    );
};

export default ItemDetailsTagsSection;
